"use client";

import { LucideIcon } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";

export interface WhoItsForSegment {
  title: string;
  body: string;
  icon: LucideIcon;
  accent: string;
}

export interface WhoItsForSectionProps {
  id?: string;
  eyebrow: string;
  title: string;
  segments: WhoItsForSegment[];
  accent: string;
  gridCols?: 2 | 3;
}

export function WhoItsForSection({
  id = "who-its-for",
  eyebrow,
  title,
  segments,
  accent,
  gridCols = 3,
}: WhoItsForSectionProps) {
  return (
    <Section id={id} className="relative">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div
          className="absolute bottom-1/4 left-1/4 h-80 w-80 rounded-full opacity-15 blur-[120px]"
          style={{ background: accent }}
        />
      </div>

      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny" style={{ color: accent }}>
              {eyebrow}
            </span>
            <h2 className="mt-3">{title}</h2>
          </div>
        </Reveal>

        <div className={`mt-12 grid gap-5 ${gridCols === 2 ? "sm:grid-cols-2" : "sm:grid-cols-2 lg:grid-cols-3"}`}>
          {segments.map((s) => {
            const Icon = s.icon;
            return (
              <Reveal key={s.title} delay={60}>
                <div
                  className="glass rounded-2xl p-6 h-full"
                  style={{
                    boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${s.accent} 12%, transparent)`,
                  }}
                >
                  <span
                    className="grid place-items-center h-11 w-11 rounded-xl"
                    style={{
                      background: `color-mix(in oklch, ${s.accent} 18%, transparent)`,
                      boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${s.accent} 35%, transparent)`,
                    }}
                  >
                    <Icon className="h-5 w-5" style={{ color: s.accent }} />
                  </span>
                  <h3 className="mt-4 text-lg">{s.title}</h3>
                  <p className="mt-3 text-sm text-[rgb(var(--fg-muted))] leading-relaxed">{s.body}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </Container>
    </Section>
  );
}
